import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent } from '@mui/material'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment, { Moment } from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { IAllowedInputDates } from '../../../../context/report-periods/report-periods.model';
import GroupService from '../../../../shared/services/group/group.service';
import { IGroupWithCategory } from '../../../../shared/services/group/group.model';
import { sortGroupsByGhgCategory, formatGroupLabel } from '../../../../shared/constants/ghg-category';
import { ICategory } from '../../../../shared/services/category/category.model';
import { IEditFileUploadLineDirectEmission, IFileUploadLineDirectEmission } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model';
import { DialogSaveButtons } from '../../../presentation-components/miscellaneous-components/dialog-save-buttons';
import CustomFieldService from '../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../shared/services/custom-field/custom-field.model';
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service';
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model';
import { getSelectableSubsidiaries } from '../../../../shared/utils/subsidiary-utils';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import DataTypeService from '../../../../shared/services/data-types/data-types.service';
import { IDataTypeRelation } from '../../../../shared/services/data-types/data-types.model';
import { EmissionsInputAdvanced } from '../../../presentation-components/emissons-components/overview/emissions-input/emissions-input-advanced-form';
import { formatTransaction } from '../../../../shared/utils/format-post-utils';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { isEtsGroupId } from '../../../../shared/utils/group-ids-utils';
import { getCategoryDisplayName } from '../../../../shared/utils/category-utils';

export const EditUploadLineDirectEmissionDialog:
  FunctionComponent<{
    editUploadLine: IFileUploadLineDirectEmission,
    open: boolean,
    closeHandler: () => void,
    submitHandler: (lineId: string, editUploadLine: IEditFileUploadLineDirectEmission) => void,
    isSubmitting: boolean,
  }> = ({
    editUploadLine,
    open,
    closeHandler,
    submitHandler,
    isSubmitting,
  }) => {
    //Services
    const subsidiaryService = SubsidiaryService.getInstance()
    const groupService = new GroupService()
    const customFieldService = new CustomFieldService()
    const dataTypeService = new DataTypeService()
    //Context
    const userDetailsContext = useContext(UserDetailsContext)
    const organisationDetails = userDetailsContext.userDetails?.organisation
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const reportPeriodContext = useContext(ReportPeriodContext)
    //State
    const [dataTypes, setDataTypes] = useState<IDataTypeRelation[]>()
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>()
    const [groups, setGroups] = useState<IGroupWithCategory[]>()
    const [allowedCategories, setAllowedCategories] = useState<ICategory[]>()
    const [customFields, setCustomFields] = useState<ICustomField[]>()
    const [openStartDate, setOpenStartDate] = useState(false);
    const [openEndDate, setOpenEndDate] = useState(false);
    const [allowedDates, setAllowedDates] = useState<IAllowedInputDates>({ startDate: undefined, endDate: undefined })
    //Use Effects
    useEffect(() => {
      if (editUploadLine.reportPeriodId && reportPeriodContext.allReportPeriods) {
        const reportPeriod = reportPeriodContext.allReportPeriods.find(rp => rp.id === editUploadLine.reportPeriodId)
        if (reportPeriod) {
          setAllowedDates({
            startDate: reportPeriod.startDate,
            endDate: reportPeriod.endDate,
          })
        }
      }
    }, [editUploadLine.reportPeriodId, reportPeriodContext.allReportPeriods])

    useEffect(() => {
      formik.setValues({
        groupId: editUploadLine.groupId,
        categoryId: editUploadLine.categoryId,
        unitsConsumed: editUploadLine.unitsConsumed,
        dataTypeId: editUploadLine.dataTypeId,
        startDate: editUploadLine.startDate,
        endDate: editUploadLine.endDate,
        energyKwh: editUploadLine.energyKwh,
        subsidiaryId: editUploadLine.subsidiaryId,
        countryId: editUploadLine.countryId,
        projectId: editUploadLine.projectId,
        locationId: editUploadLine.locationId,
        contactSupplierId: editUploadLine.contactSupplierId,
        vehicleId: editUploadLine.vehicleId,
        partOfEts: editUploadLine.regulatedEmissionTradingSchemeName ? true : false,
        regulatedEmissionTradingSchemeId: editUploadLine.regulatedEmissionTradingSchemeId,
        isFlag: editUploadLine.isFlag === true,
        flagCategory: editUploadLine.flagCategory ?? '',
      })

      subsidiaryService.getAll()
        .then(response => {
          setSubsidiaries(response)
        })
        .catch(err => console.log(err))

      dataTypeService.getAll()
        .then(response => {
          setDataTypes(response)
        })
        .catch(err => console.log(err))

      customFieldService.getAllActive(`&entryType=transaction`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => console.log(err))
    }, [])

    useEffect(() => {
      if (customFields && customFields.length && editUploadLine?.customFieldValues) {
        customFields.forEach(field => {
          const matchingValue = editUploadLine.customFieldValues.find(v => v.customFieldId === field.id)?.value ?? '';
          formik.setFieldValue(field.id, matchingValue, false);
        });
      }
    }, [customFields, editUploadLine]);

    useEffect(() => {
      groupService.getGroupsWithCategories(false)
        .then(response => {
          setGroups(response)
          const group = response.find(group => group.id === formik.values?.groupId)
          if (group) {
            setAllowedCategories([...group.categories])
            const preselectedCategory = group.categories.find(category => category.id === formik.values.categoryId)
            if (preselectedCategory) {
            } else {
              formik.setFieldValue('categoryId', '')
            }
          }
        })
        .catch(err => console.log(err))
    }, [])
    //Formik
    const editUploadSchema = Yup.object().shape({
      groupId: Yup.string().required('Group is required'),
      categoryId: Yup.string().nullable(),
      dataTypeId: Yup.string().required('Data type is required'),
      unitsConsumed: Yup.number().min(0.01, 'The amount must be greater than 0').required('You must provide an amount'),
      startDate: Yup.string().required('Start date is required'),
      endDate: Yup.string()
        .required('End date is required')
        .test('not-before-start', 'End date cannot be before start date', function (value) {
          if (!value || !this.parent.startDate) return true;
          return moment(value).isSameOrAfter(moment(this.parent.startDate), 'day');
        }),
      energyKwh: Yup.number().min(0.0001, 'The energy consumption must be greater than 0').nullable(),
      subsidiaryId: Yup.string().required(`${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} is required`),
      countryId: Yup.string().required('Country is required'),
      projectId: Yup.string().nullable(),
      locationId: Yup.string().nullable(),
      contactSupplierId: Yup.string().nullable(),
      vehicleId: Yup.string().nullable(),
      partOfEts: Yup.boolean(),
      regulatedEmissionTradingSchemeId: Yup.string().when('partOfEts', {
        is: true,
        then: schema => schema.required('You need to select a regulated emission trading scheme'),
        otherwise: schema => schema.nullable().notRequired()
      })
    })

    const formik = useFormik<any>({
      initialValues: {
        groupId: editUploadLine.groupId,
        categoryId: editUploadLine.categoryId,
        unitsConsumed: editUploadLine.unitsConsumed,
        dataTypeId: editUploadLine.dataTypeId,
        startDate: editUploadLine.startDate,
        endDate: editUploadLine.endDate,
        energyKwh: editUploadLine.energyKwh,
        subsidiaryId: editUploadLine.subsidiaryId,
        countryId: editUploadLine.countryId,
        projectId: editUploadLine.projectId,
        locationId: editUploadLine.locationId,
        contactSupplierId: editUploadLine.contactSupplierId,
        vehicleId: editUploadLine.vehicleId,
        partOfEts: editUploadLine.regulatedEmissionTradingSchemeName ? true : false,
        regulatedEmissionTradingSchemeId: editUploadLine.regulatedEmissionTradingSchemeId,
        isFlag: editUploadLine.isFlag === true,
        flagCategory: editUploadLine.flagCategory ?? '',
      },
      validationSchema: editUploadSchema,
      onSubmit: (values) => {
        const response = formatTransaction(values, values.groupId, true, customFields, formik.values?.categoryId)
        submitHandler(editUploadLine.id, response)
      }
    });

    //Formik Event Handlers
    const dateHandler = (date: Moment | null, name: string) => {
      if (date) {
        formik.setFieldValue(name, date.format('YYYY-MM-DD'));
      }
    }

    const subsidiaryHandler = (e: SelectChangeEvent<string>) => {
      const subsidiary = subsidiaries?.find(s => s.id === e.target.value)
      if (subsidiary) {
        formik.setFieldValue('subsidiaryId', subsidiary.id)
        formik.setFieldValue('countryId', subsidiary.countryId)
      }
    }

    const groupHandler = (value: string) => {
      const oldGroup = groups?.find(group => group.id === formik.values.groupId)
      const newGroup = groups?.find(group => group.id === value)
      if (oldGroup?.id !== value) {
        formik.setFieldValue('groupId', value);
        formik.setFieldValue('categoryId', '')
        setAllowedCategories(newGroup?.categories)
      }
    }

    //Component Event Handlers
    const cancelHandler = () => {
      formik.resetForm();
      closeHandler();
    }

    return (
      <Dialog open={open} onClose={closeHandler} maxWidth='xs'>
        <Box sx={{ p: 4 }} component='form'>
          <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>
            Edit Uploaded Data
          </DialogTitle>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            {groups && <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='groupId'>Group</InputLabel>
              <Select
                id='groupId' label='Group' name='groupId'
                value={formik.values.groupId || ''}
                onChange={(e) => groupHandler(e.target.value)}
                error={formik.touched.groupId && Boolean(formik.errors.groupId)}
                sx={{ color: 'text.primary', }}>
                {sortGroupsByGhgCategory(groups).map((group, index) =>
                  <MenuItem key={index} value={group.id}>{formatGroupLabel(group)}</MenuItem>
                )}
              </Select>
              {formik.touched.groupId && formik.errors.groupId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.groupId ? <div>{formik.errors.groupId as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>}
            {allowedCategories && <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='categoryId'> Category</InputLabel>
              <Select
                id='categoryId' label='Category' name='categoryId'
                value={formik.values.categoryId || ''}
                onChange={formik.handleChange}
                error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
                sx={{ color: 'text.primary', }}>
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {allowedCategories.sort((a, b) => a.name.localeCompare(b.name)).map((category, index) =>
                  <MenuItem key={index} value={category.id}>{getCategoryDisplayName(category)}</MenuItem>
                )}
              </Select>
              {formik.touched.categoryId && formik.errors.categoryId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.categoryId ? <div>{formik.errors.categoryId as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>}
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='unitsConsumed'>Units Consumed</InputLabel>
              <OutlinedInput inputProps={{ min: 0 }} id='unitsConsumed' label='Units Consumed' name='unitsConsumed' type='number'
                value={formik.values.unitsConsumed || ''}
                onChange={formik.handleChange}
                endAdornment={<InputAdornment position='end'>kg CO2e</InputAdornment>}
                error={formik.touched.unitsConsumed && Boolean(formik.errors.unitsConsumed)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.unitsConsumed && formik.errors.unitsConsumed &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.unitsConsumed ? <div>{formik.errors.unitsConsumed as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
            {(formik.values.groupId === '22cc8b45-aa77-49aa-8fb2-c39bb884fa13' || formik.values.groupId === '504daea8-fd6b-4b06-8e5f-dc1a90558ede' || formik.values.groupId === 'acf9ae04-25d9-43d7-b442-fabd69e5844b') &&
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='energyKwh'>Energy Consumption</InputLabel>
                <OutlinedInput inputProps={{ min: 0 }} id='energyKwh' label='Energy Consumption' name='energyKwh' type='number'
                  value={formik.values.energyKwh || ''}
                  onChange={formik.handleChange}
                  endAdornment={<InputAdornment position='end'>kWh</InputAdornment>}
                  error={formik.touched.energyKwh && Boolean(formik.errors.energyKwh)}
                  sx={{ color: 'text.primary' }}></OutlinedInput>
                {formik.touched.energyKwh && formik.errors.energyKwh &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.energyKwh ? <div>{formik.errors.energyKwh as string}</div> : null}
                  </FormHelperText>
                }
              </FormControl>}
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label='Start Date'
                  value={formik.values.startDate ? moment(formik.values.startDate) : null}
                  onChange={(date) => dateHandler(date, 'startDate')}
                  format='DD/MM/YYYY'
                  maxDate={moment(allowedDates.endDate)}
                  minDate={moment(allowedDates.startDate)}
                  sx={{ color: 'text.primary' }}
                  open={openStartDate}
                  onOpen={() => setOpenStartDate(true)}
                  onClose={() => setOpenStartDate(false)}
                  slotProps={{
                    textField: {
                      onClick: () => setOpenStartDate(true),
                      error: formik.touched.startDate && Boolean(formik.errors.startDate)
                    },
                  }}
                />
              </LocalizationProvider>
              {formik.touched.startDate && formik.errors.startDate &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.startDate ? <div>{formik.errors.startDate as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  disabled={formik.values.startDate ? false : true}
                  label='End Date'
                  maxDate={moment(allowedDates.endDate)}
                  minDate={moment(formik.values.startDate) || moment(allowedDates.startDate)}
                  value={formik.values.endDate ? moment(formik.values.endDate) : null}
                  onChange={(date) => dateHandler(date, 'endDate')}
                  format='DD/MM/YYYY'
                  sx={{ color: 'text.primary' }}
                  open={openEndDate}
                  onOpen={() => setOpenEndDate(true)}
                  onClose={() => setOpenEndDate(false)}
                  slotProps={{
                    textField: {
                      onClick: () => setOpenEndDate(true),
                      error: formik.touched.endDate && Boolean(formik.errors.endDate),
                    },
                  }}
                />
              </LocalizationProvider>
              {formik.touched.endDate && formik.errors.endDate &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.endDate ? <div>{formik.errors.endDate as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='dataTypeId'>Data Type</InputLabel>
              <Select
                id='dataTypeId' label='Data Type' name='dataTypeId' type='text'
                value={formik.values.dataTypeId || ''}
                onChange={formik.handleChange}
                error={formik.touched.dataTypeId && Boolean(formik.errors.dataTypeId)}
                sx={{ color: 'text.primary', }}>
                {dataTypes?.map((dataType, index) =>
                  <MenuItem key={index} value={dataType.id}>{dataType.dataType}</MenuItem>
                )}
              </Select>
              {formik.touched.dataTypeId && formik.errors.dataTypeId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.dataTypeId ? <div>{formik.errors.dataTypeId as string}</div> : null}
                </FormHelperText>
              }
              {subsidiaries && (organisationDetails?.enableSubsidiaries || subsidiaries.length > 1) &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='subsidiaryId'>{screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}</InputLabel>
                  <Select
                    id='subsidiaryId' label={screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} name='subsidiaryId'
                    value={formik.values.subsidiaryId || ''}
                    onChange={subsidiaryHandler}
                    error={formik.touched.subsidiaryId && Boolean(formik.errors.subsidiaryId)}
                    sx={{ color: 'text.primary' }}>
                    {getSelectableSubsidiaries(subsidiaries, formik.values.subsidiaryId).sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary, index) =>
                      <MenuItem key={index} value={subsidiary.id}>{subsidiary.name}</MenuItem>
                    )}
                  </Select>
                  {formik.touched.subsidiaryId && formik.errors.subsidiaryId &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.subsidiaryId ? <div>{formik.errors.subsidiaryId as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>}
            </FormControl>
            {customFields &&
              <EmissionsInputAdvanced
                isEdit={true}
                customFields={customFields}
                formik={formik}
                hideRegulatedEmissionTradingScheme={isEtsGroupId(formik.values.groupId)}
                hideRec={true}
                hideEnergySupplierTariff={true}
                hideCustomBasedMarketMultiplier={true}
                hideExcludedFromReporting={true}
              />}
          </DialogContent>
          <DialogActions sx={{ p: 0 }}>
            <DialogSaveButtons
              cancelHandler={cancelHandler}
              submitHandler={formik.submitForm}
              isSubmitting={isSubmitting}
            />
          </DialogActions>
        </Box>
      </Dialog>
    )
  }